import { useMemo, useCallback, useState, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import moment from "moment";
import { AxiosResponse } from "axios";
import { getData } from "../Services/getData";
import { endpoints } from "../Utils/UrlEnum";
import { IClientTimeEntry, IExamClientReviewPanelProps } from "../Utils/Types";
import { Vocabulary } from "../Utils/Vocabulary";
import styles from "../Styles/examClientReviewPanel.module.css";

const DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 2;

export default function ExamClientReviewPanel({
  entries,
  onChange,
}: IExamClientReviewPanelProps) {
  const [activeAddTime, setActiveAddTime] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const grouped = useMemo(
    () =>
      entries.reduce<Record<string, IClientTimeEntry[]>>((acc: Record<string, IClientTimeEntry[]>, entry: IClientTimeEntry) => {
        if (!acc[entry.time]) acc[entry.time] = [];
        acc[entry.time].push(entry);
        return acc;
      }, {}),
    [entries]
  );

  const handleTimeChange = useCallback(
    (oldTime: string, newDate: Date | null) => {
      if (!newDate) return;
      const newTime = moment(newDate).format("HH:mm");
      onChange((prev: IClientTimeEntry[]) => prev.map((e: IClientTimeEntry) => (e.time === oldTime ? { ...e, time: newTime } : e)));
    },
    [onChange]
  );

  const handleRemove = useCallback(
    (clientId: string) => {
      onChange((prev: IClientTimeEntry[]) => prev.filter((e: IClientTimeEntry) => e.clientId !== clientId));
    },
    [onChange]
  );

  const handleSearchInput = useCallback((_: any, value: string, reason: string) => {
    if (reason !== "input") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      getData(`${endpoints.client.search}${value}`).then((res: AxiosResponse) => {
        setSearchResults(res?.data || []);
      });
    }, DEBOUNCE_MS);
  }, []);

  const handleOpenAdd = useCallback((time: string) => {
    setActiveAddTime(time);
    setSelectedClient(null);
    setSearchResults([]);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setActiveAddTime(null);
    setSelectedClient(null);
    setSearchResults([]);
  }, []);

  const handleConfirmAdd = useCallback(
    (time: string) => {
      if (!selectedClient) return;
      onChange((prev: IClientTimeEntry[]) => [
        ...prev,
        {
          clientId: selectedClient._id,
          firstName: selectedClient.firstName,
          lastName: selectedClient.lastName,
          time,
        },
      ]);
      setActiveAddTime(null);
      setSelectedClient(null);
      setSearchResults([]);
    },
    [selectedClient, onChange]
  );

  return (
    <Box className={styles.container}>
      <Box>
        <Typography variant="h6">{Vocabulary.examClientsReview}</Typography>
        <Typography className={styles.hint}>{Vocabulary.examReviewHint}</Typography>
        <Typography variant="subtitle2" fontWeight={600}>
          {Vocabulary.examClientsFoundCount(entries.length)}
        </Typography>
      </Box>
      <Divider />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
        {Object.entries(grouped).map(([time, clients]) => (
          <Box key={time} className={styles.group}>
            <Box className={styles.groupHeader}>
              <Typography variant="subtitle1" fontWeight={600}>
                {Vocabulary.hour}:
              </Typography>
              <TimePicker
                value={moment(time, "HH:mm").toDate()}
                onChange={(date) => handleTimeChange(time, date)}
                ampm={false}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
            {clients.map((client) => (
              <Box key={client.clientId} className={styles.clientRow}>
                <Typography className={styles.clientName}>
                  {`${client.lastName} ${client.firstName}`}
                </Typography>
                <IconButton
                  onClick={() => handleRemove(client.clientId)}
                  color="error"
                  size="small"
                  aria-label={Vocabulary.delete}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            {activeAddTime === time ? (
              <Box className={styles.addRow}>
                <Autocomplete
                  options={searchResults}
                  value={selectedClient}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: any) =>
                    `${option.lastName} ${option.firstName}` || ""
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option._id === value._id
                  }
                  onChange={(_, value) => setSelectedClient(value)}
                  onInputChange={handleSearchInput}
                  noOptionsText={Vocabulary.noData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={Vocabulary.searchStudent}
                      size="small"
                      fullWidth
                    />
                  )}
                  className={styles.addClientSearch}
                />
                <IconButton
                  color="primary"
                  onClick={() => handleConfirmAdd(time)}
                  disabled={!selectedClient}
                  aria-label={Vocabulary.confirm}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  onClick={handleCancelAdd}
                  size="small"
                  aria-label={Vocabulary.cancelAction}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Box className={styles.addTrigger}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAdd(time)}
                >
                  {Vocabulary.addNewStudent}
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </LocalizationProvider>
    </Box>
  );
}
